/**
 * Standardized React Query timing presets for the AIS UI codebase.
 *
 * Choose the preset that matches your data's volatility:
 * - REALTIME:  Live data that must always be fresh (ship positions, monitoring)
 * - FREQUENT:  Data that changes often but tolerates brief staleness (weather, arrivals)
 * - MODERATE:  Data that changes periodically (ship details, port info, configs)
 * - STATIC:    Data that rarely changes (settings, translations, schemas)
 * - IMMUTABLE: Data that never changes once fetched (images, historical records)
 */

/** How long data is considered fresh before React Query will refetch */
export const STALE_TIMES = {
  /** Always refetch — live/realtime data (0ms) */
  REALTIME: 0,
  /** Tolerates 30s staleness — frequently updating data */
  FREQUENT: 30 * 1000,
  /** Tolerates 5min staleness — periodically changing data */
  MODERATE: 5 * 60 * 1000,
  /** Tolerates 30min staleness — rarely changing data */
  STATIC: 30 * 60 * 1000,
  /** Never refetches — immutable data */
  IMMUTABLE: Infinity,
} as const;

/** How long inactive query data is kept in memory before garbage collection */
export const GC_TIMES = {
  /** No caching — immediately garbage-collect when query becomes inactive */
  NONE: 0,
  /** Short retention for transient data (5 min) */
  SHORT: 5 * 60 * 1000,
  /** Standard retention (15 min — React Query default) */
  STANDARD: 15 * 60 * 1000,
  /** Extended retention for expensive queries (30 min) */
  EXTENDED: 30 * 60 * 1000,
  /** Long retention for rarely changing data (1 hour) */
  LONG: 60 * 60 * 1000,
  /** Never garbage collect */
  FOREVER: Infinity,
} as const;

/** Polling intervals for queries that need periodic background refetching */
export const REFETCH_INTERVALS = {
  /** Fast polling for real-time data (5s) */
  FAST: 5 * 1000,
  /** Standard polling (30s) */
  STANDARD: 30 * 1000,
  /** Slow polling for background data (1 min) */
  SLOW: 60 * 1000,
} as const;

/**
 * Pre-built query option presets combining timing + behavior.
 * Spread these into your useQuery options for consistent defaults.
 *
 * @example
 * ```ts
 * useQuery({
 *   queryKey: ['ship', mmsi],
 *   queryFn: () => fetchShip(mmsi),
 *   ...QUERY_PRESETS.moderate,
 * })
 * ```
 */
export const QUERY_PRESETS = {
  /** For live/realtime data: always fresh, fast polling, refetch in background */
  realtime: {
    staleTime: STALE_TIMES.REALTIME,
    refetchInterval: REFETCH_INTERVALS.FAST,
    refetchIntervalInBackground: true,
  },
  /** For frequently changing data: 30s stale, standard polling */
  frequent: {
    staleTime: STALE_TIMES.FREQUENT,
    refetchInterval: REFETCH_INTERVALS.STANDARD,
  },
  /** For periodically changing data: 5min stale, no refetch on focus */
  moderate: {
    staleTime: STALE_TIMES.MODERATE,
    refetchOnWindowFocus: false,
  },
  /** For rarely changing data: 30min stale, extended gc, no auto-refetch */
  static: {
    staleTime: STALE_TIMES.STATIC,
    gcTime: GC_TIMES.LONG,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  },
  /** For immutable data: never refetch, never gc */
  immutable: {
    staleTime: STALE_TIMES.IMMUTABLE,
    gcTime: GC_TIMES.FOREVER,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  },
} as const;
